import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "../../config";
import {
    Users,
    UserPlus,
    TrendingUp,
    Calendar,
    CalendarDays,
    Activity,
    BarChart3,
    User,
    Bell,
    Award,
    Target,
    BookOpen,
    CheckCircle,
    AlertTriangle,
    Clock,
    FileText,
    Settings,
    Megaphone,
    PieChart,
    LineChart,
    Shield,
    Database,
    Zap,
    ArrowUp,
    ArrowDown,
    Eye,
    ChevronRight,
    AlertCircle,
    Info,
    X
} from "lucide-react";

import ManagementDashboard from "./ManagementDashboard";

const HomePage = () => {
    // ... existing state ...
    const [dashboardStats, setDashboardStats] = useState(null);
    const [systemHealth, setSystemHealth] = useState(null);
    const [branchOverview, setBranchOverview] = useState(null);
    const [systemOverview, setSystemOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedTimeframe, setSelectedTimeframe] = useState('week');
    const [dismissedAlerts, setDismissedAlerts] = useState(new Set());

    // Get user role from localStorage
    const userRole = localStorage.getItem('user_role') || 'admin';

    // If management role, render Management Dashboard immediately
    // Note: We move this check to the render phase to comply with React Hooks rules
    // Hooks must be called in the same order every time, so we can't return early before useEffect

    // API base URL imported from config

    // API helper function
    const apiCall = async (endpoint, options = {}) => {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                ...options.headers
            },
            ...options
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `HTTP ${response.status}`);
        }

        return response.json();
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError('');

                const [dashboardData, healthData] = await Promise.all([
                    apiCall('/admin/dashboard/comprehensive-stats'),
                    apiCall('/admin/dashboard/system-health').catch(() => null) // Optional
                ]);

                setDashboardStats(dashboardData);
                setSystemHealth(healthData);

                // Fetch system overview for admin/management roles
                const role = localStorage.getItem('user_role') || 'admin';
                if (role === 'admin' || role === 'management') {
                    try {
                        const overviewData = await apiCall('/admin/dashboard/system-overview');
                        setSystemOverview(overviewData);
                    } catch (e) {
                        console.log('System overview not available:', e.message);
                    }
                }
            } catch (error) {
                console.error("Error fetching dashboard data:", error);
                setError('Failed to load dashboard data. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Dismiss alert
    const dismissAlert = (alertId) => {
        setDismissedAlerts(prev => new Set([...prev, alertId]));
    };

    // Get action icon
    const getActionIcon = (iconName, color) => {
        const iconColor = getIconColor(color);
        const icons = {
            calendar: <Calendar className={`w-4 h-4 ${iconColor}`} />,
            book: <BookOpen className={`w-4 h-4 ${iconColor}`} />,
            grid: <BarChart3 className={`w-4 h-4 ${iconColor}`} />,
            users: <Users className={`w-4 h-4 ${iconColor}`} />,
            'graduation-cap': <User className={`w-4 h-4 ${iconColor}`} />,
            chart: <BarChart3 className={`w-4 h-4 ${iconColor}`} />,
            target: <Target className={`w-4 h-4 ${iconColor}`} />,
            trophy: <Award className={`w-4 h-4 ${iconColor}`} />,
            filetext: <FileText className={`w-4 h-4 ${iconColor}`} />
        };
        return icons[iconName] || <Settings className={`w-4 h-4 ${iconColor}`} />;
    };

    // Get action color
    const getActionColor = (color) => {
        const colors = {
            blue: '#dbeafe',
            green: '#dcfce7',
            purple: '#f3e8ff',
            orange: '#fed7aa',
            indigo: '#e0e7ff',
            teal: '#ccfbf1',
            red: '#fee2e2',
            yellow: '#fef3c7'
        };
        return colors[color] || colors.blue;
    };

    // Get icon color
    const getIconColor = (color) => {
        const colors = {
            blue: 'text-blue-600',
            green: 'text-green-600',
            purple: 'text-purple-600',
            orange: 'text-orange-600',
            indigo: 'text-indigo-600',
            teal: 'text-teal-600',
            red: 'text-red-600',
            yellow: 'text-yellow-600'
        };
        return colors[color] || 'text-blue-600';
    };

    // Get alert icon
    const getAlertIcon = (type) => {
        const icons = {
            warning: <AlertTriangle className="w-5 h-5" />,
            error: <AlertCircle className="w-5 h-5" />,
            info: <Info className="w-5 h-5" />,
            success: <CheckCircle className="w-5 h-5" />
        };
        return icons[type] || icons.info;
    };

    // Get alert colors
    const getAlertColors = (type) => {
        const colors = {
            warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
            error: 'bg-red-50 border-red-200 text-red-800',
            info: 'bg-blue-50 border-blue-200 text-blue-800',
            success: 'bg-green-50 border-green-200 text-green-800'
        };
        return colors[type] || colors.info;
    };

    // Conditional render for Management users
    if (userRole === 'management') {
        return <ManagementDashboard />;
    }

    if (loading) {
        return (
            <>
                <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
                    <div className="w-full">
                        <div className="flex items-center justify-center h-64">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D67C2C] mx-auto mb-4"></div>
                                <p className="text-gray-600">Loading comprehensive dashboard data...</p>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (!dashboardStats) {
        return (
            <>
                <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
                    <div className="w-full">
                        <div className="text-center py-12">
                            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-300" />
                            <p className="text-lg font-medium text-red-600">Failed to load dashboard data</p>
                            <p className="text-sm text-red-400">{error}</p>
                            <button
                                onClick={() => window.location.reload()}
                                className="mt-4 px-4 py-2 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] transition-colors"
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
                <div className="w-full px-4 sm:px-6 lg:px-8 py-8">

                    {/* Header */}
                    <div className="pt-8 pb-8 text-center relative overflow-hidden">
                        <div className="relative z-10">
                            <div>
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: '#D67C2C' }}>
                                    <Shield className="w-8 h-8 text-white" />
                                </div>
                                <h1 className="text-5xl font-bold bg-[#D67C2C] bg-clip-text text-transparent mb-4">
                                    Admin Command Center
                                </h1>
                                <p className="text-[#1D3469] text-xl font-medium max-w-3xl mx-auto px-4">
                                    Complete oversight and control of your educational institution
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* System Alerts */}
                    {dashboardStats.systemAlerts && dashboardStats.systemAlerts.length > 0 && (
                        <div className="mb-8">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <Bell className="w-5 h-5 text-[#D67C2C]" />
                                System Alerts
                            </h2>
                            <div className="space-y-3">
                                {dashboardStats.systemAlerts
                                    .filter(alert => !dismissedAlerts.has(alert.id))
                                    .map((alert) => (
                                        <div key={alert.id} className={`border rounded-lg p-4 ${getAlertColors(alert.type)}`}>
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-start gap-3">
                                                    {getAlertIcon(alert.type)}
                                                    <div>
                                                        <h4 className="font-semibold">{alert.title}</h4>
                                                        <p className="text-sm mt-1">{alert.message}</p>
                                                        <div className="flex items-center gap-4 mt-2 text-xs">
                                                            <span>Priority: {alert.priority}</span>
                                                            <span>{new Date(alert.timestamp).toLocaleString()}</span>
                                                            {alert.actionRequired && (
                                                                <span className="font-semibold">Action Required</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => dismissAlert(alert.id)}
                                                    className="p-1 hover:bg-black hover:bg-opacity-10 rounded"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}

                    {/* System Overview Section - Campus Details for Admin/Management */}
                    {systemOverview && systemOverview.current_campus && (
                        <div className="mb-10">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <Database className="w-5 h-5 text-[#D67C2C]" />
                                Campus Overview
                                <span className="text-sm font-normal text-gray-500 ml-2">
                                    {systemOverview.current_campus.campus_name}
                                </span>
                            </h2>

                            {/* Campus Info Card */}
                            <div className="bg-gradient-to-r from-[#D67C2C] to-[#e89d4f] rounded-2xl p-6 text-white mb-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="text-2xl font-bold">{systemOverview.current_campus.campus_name}</h3>
                                        <div className="flex items-center gap-4 mt-2">
                                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${systemOverview.current_campus.status === 'active' ? 'bg-green-500/30' :
                                                'bg-white/20'
                                                }`}>
                                                {systemOverview.current_campus.status}
                                            </span>
                                            <span className="text-white/80">
                                                {systemOverview.current_campus.school_type === 'single' ? 'Single School' :
                                                    systemOverview.current_campus.school_type === 'group_parent' ? 'School Group (Parent)' :
                                                        'Branch Campus'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-4xl font-bold">{systemOverview.current_campus.total_users}</p>
                                        <p className="text-white/80">Total Users</p>
                                    </div>
                                </div>
                            </div>

                            {/* Role Breakdown */}
                            {systemOverview.current_campus.role_breakdown && systemOverview.current_campus.role_breakdown.length > 0 && (
                                <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                        <Users className="w-5 h-5 text-[#D67C2C]" />
                                        User Breakdown by Role
                                    </h3>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                                        {systemOverview.current_campus.role_breakdown.map((role) => (
                                            <div key={role.role} className="bg-gray-50 rounded-lg p-4 text-center hover:bg-orange-50 transition-colors">
                                                <p className="text-2xl font-bold text-gray-900">{role.count}</p>
                                                <p className="text-sm font-medium text-gray-700">{role.display_name}</p>
                                                <div className="flex justify-center gap-2 mt-1 text-xs">
                                                    <span className="text-green-600">{role.active_count} active</span>
                                                    {role.pending_count > 0 && (
                                                        <span className="text-yellow-600">{role.pending_count} pending</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Content Stats */}
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                                <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-gray-500">Lesson Plans</p>
                                            <p className="text-2xl font-bold text-blue-600">{systemOverview.current_campus.total_lesson_plans}</p>
                                            <div className="flex gap-2 text-xs mt-1">
                                                <span className="text-green-600">{systemOverview.current_campus.lesson_plans_taught} taught</span>
                                                <span className="text-gray-500">{systemOverview.current_campus.lesson_plans_draft} draft</span>
                                            </div>
                                        </div>
                                        <div className="p-2 bg-blue-100 rounded-lg">
                                            <BookOpen className="w-5 h-5 text-blue-600" />
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-gray-500">Assessments</p>
                                            <p className="text-2xl font-bold text-green-600">{systemOverview.current_campus.total_assessments}</p>
                                            <div className="flex gap-2 text-xs mt-1">
                                                <span className="text-green-600">{systemOverview.current_campus.assessments_published} published</span>
                                                <span className="text-gray-500">{systemOverview.current_campus.assessments_draft} draft</span>
                                            </div>
                                        </div>
                                        <div className="p-2 bg-green-100 rounded-lg">
                                            <FileText className="w-5 h-5 text-green-600" />
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-gray-500">Classes</p>
                                            <p className="text-2xl font-bold text-purple-600">{systemOverview.current_campus.total_classes}</p>
                                        </div>
                                        <div className="p-2 bg-purple-100 rounded-lg">
                                            <Target className="w-5 h-5 text-purple-600" />
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-gray-500">This Week</p>
                                            <p className="text-2xl font-bold text-[#D67C2C]">+{systemOverview.current_campus.new_users_this_week}</p>
                                            <p className="text-xs text-gray-500">new users</p>
                                        </div>
                                        <div className="p-2 bg-orange-100 rounded-lg">
                                            <TrendingUp className="w-5 h-5 text-[#D67C2C]" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Teaching Assignments */}
                            {systemOverview.current_campus.teaching_assignments && systemOverview.current_campus.teaching_assignments.length > 0 && (
                                <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                        <User className="w-5 h-5 text-[#D67C2C]" />
                                        Teacher Activity
                                    </h3>
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Teacher</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Lesson Plans</th>
                                                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Assessments</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {systemOverview.current_campus.teaching_assignments.map((teacher) => (
                                                    <tr key={teacher.teacher_id} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3">
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-8 h-8 rounded-full bg-[#D67C2C] flex items-center justify-center text-white text-sm font-semibold">
                                                                    {teacher.teacher_name?.charAt(0)?.toUpperCase()}
                                                                </div>
                                                                <span className="font-medium text-gray-900">{teacher.teacher_name}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className={`px-2 py-1 rounded text-xs font-medium ${teacher.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                                                                }`}>
                                                                {teacher.is_active ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <span className="font-semibold text-blue-600">{teacher.lesson_plan_count}</span>
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <span className="font-semibold text-green-600">{teacher.assessment_count}</span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Key Metrics Overview */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                        {/* User Stats */}
                        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
                            <div className="p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Total Users</p>
                                        <p className="text-3xl font-bold text-blue-600 mt-1">{dashboardStats.userStats.totalUsers}</p>
                                        <div className="flex items-center gap-2 mt-2">
                                            <ArrowUp className="w-4 h-4 text-green-500" />
                                            <span className="text-sm text-green-600">{dashboardStats.userStats.userGrowthRate}%</span>
                                        </div>
                                    </div>
                                    <div className="p-3 rounded-full bg-blue-100">
                                        <Users className="w-6 h-6 text-blue-600" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Competency Stats */}
                        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
                            <div className="p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Competencies</p>
                                        <p className="text-3xl font-bold text-green-600 mt-1">{dashboardStats.academicStats.totalCompetenciesAssigned || 0}</p>
                                        <div className="flex items-center gap-2 mt-2">
                                            <span className="text-sm text-gray-600">Completion: {dashboardStats.academicStats.competencyCompletionRate || 0}%</span>
                                        </div>
                                    </div>
                                    <div className="p-3 rounded-full bg-green-100">
                                        <Target className="w-6 h-6 text-green-600" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Calendar Stats */}
                        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
                            <div className="p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Task Completion</p>
                                        <p className="text-3xl font-bold text-purple-600 mt-1">{dashboardStats.calendarStats.taskCompletionRate}%</p>
                                        <div className="flex items-center gap-2 mt-2">
                                            <span className="text-sm text-gray-600">{dashboardStats.calendarStats.completedTasks} completed</span>
                                        </div>
                                    </div>
                                    <div className="p-3 rounded-full bg-purple-100">
                                        <CheckCircle className="w-6 h-6 text-purple-600" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* System Health */}
                        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
                            <div className="p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">System Health</p>
                                        <p className="text-3xl font-bold text-orange-600 mt-1">{systemHealth?.system_status === 'operational' ? '100%' : '85%'}</p>
                                        <div className="flex items-center gap-2 mt-2">
                                            <span className="text-sm text-green-600">Operational</span>
                                        </div>
                                    </div>
                                    <div className="p-3 rounded-full bg-orange-100">
                                        <Shield className="w-6 h-6 text-orange-600" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Detailed Analytics */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                        {/* User Breakdown */}
                        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-100">
                                    <Users className="w-5 h-5 text-blue-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">User Distribution</h3>
                            </div>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <User className="w-4 h-4 text-blue-500" />
                                        <span className="text-sm text-gray-600">Teachers</span>
                                    </div>
                                    <span className="font-semibold text-gray-900">{dashboardStats.userStats.teacherCount}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Users className="w-4 h-4 text-green-500" />
                                        <span className="text-sm text-gray-600">Students</span>
                                    </div>
                                    <span className="font-semibold text-gray-900">{dashboardStats.userStats.studentCount}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <User className="w-4 h-4 text-purple-500" />
                                        <span className="text-sm text-gray-600">Parents</span>
                                    </div>
                                    <span className="font-semibold text-gray-900">{dashboardStats.userStats.parentCount}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Shield className="w-4 h-4 text-orange-500" />
                                        <span className="text-sm text-gray-600">Admins</span>
                                    </div>
                                    <span className="font-semibold text-gray-900">{dashboardStats.userStats.adminCount}</span>
                                </div>
                            </div>
                        </div>

                        {/* Academic Performance */}
                        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-100">
                                    <BookOpen className="w-5 h-5 text-green-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Academic Overview</h3>
                            </div>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Total Lesson Plans</span>
                                    <span className="font-semibold text-green-600">{dashboardStats.academicStats.totalLessonPlans}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Total Assessments</span>
                                    <span className="font-semibold text-blue-600">{dashboardStats.academicStats.totalAssessments}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Competencies Assigned</span>
                                    <span className="font-semibold text-purple-600">{dashboardStats.academicStats.totalCompetenciesAssigned || 0}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Competency Progress</span>
                                    <span className="font-semibold text-green-600">{dashboardStats.academicStats.competencyCompletionRate || 0}%</span>
                                </div>
                            </div>
                        </div>

                        {/* System Status */}
                        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-orange-100">
                                    <Database className="w-5 h-5 text-orange-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">System Status</h3>
                            </div>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Total Classes</span>
                                    <span className="font-semibold text-gray-900">{dashboardStats.systemStats.totalClasses}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Total Subjects</span>
                                    <span className="font-semibold text-gray-900">{dashboardStats.systemStats.totalSubjects}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Attendance Rate</span>
                                    <span className="font-semibold text-green-600">{dashboardStats.systemStats.attendanceRate}%</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">System Uptime</span>
                                    <span className="font-semibold text-green-600">{dashboardStats.systemStats.systemUptime}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Top Performers */}
                    {dashboardStats.topPerformers && dashboardStats.topPerformers.length > 0 && (
                        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-8">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-yellow-100">
                                    <Award className="w-5 h-5 text-yellow-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Top Performers</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {dashboardStats.topPerformers.map((performer, index) => (
                                    <div key={performer.id} className="bg-gray-50 rounded-lg p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                                {index + 1}
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="font-medium text-gray-900">{performer.name}</h4>
                                                <p className="text-sm text-gray-600">{performer.metric}: {performer.value}</p>
                                                <span className="text-xs text-gray-500 capitalize">{performer.role}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Quick Actions */}
                    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                                <Zap className="w-5 h-5 text-white" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {dashboardStats.quickActions.map((action) => (
                                <button
                                    key={action.id}
                                    onClick={() => window.location.href = action.url}
                                    className="p-4 border border-gray-200 rounded-lg hover:border-[#D67C2C] hover:bg-orange-50 transition-all duration-200 text-left group"
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center`} style={{ backgroundColor: `${getActionColor(action.color)}` }}>
                                            {getActionIcon(action.icon, action.color)}
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#D67C2C] transition-colors" />
                                    </div>
                                    <h4 className="font-medium text-gray-900 mb-1">{action.title}</h4>
                                    <p className="text-sm text-gray-600">{action.description}</p>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default HomePage;